import { useContext } from 'react';
import { isAfter } from 'date-fns';

import { cloneDeep } from 'es-toolkit';
import { List, Text } from '@zillow/constellation-10';

import { RMWContext } from '../../providers/RMWContext';

import { useSelector } from '../../selectors/useSelector';

import StringConstants from '../../constants/StringConstants';

import TextLinkContent from './TextLinkContent';
import DuplicateListingDetailsContainer from './DuplicateListingDetailsContainer';

const { FORMATTED_BRAND_NAMES: brandConstants } = StringConstants;
const DEMOTED_LISTING_COPY =
  'We found a higher quality version of this listing in your account.' +
  ' Any edits made to this version may not be seen by renters on Zillow, Trulia and HotPads. ';
const compareDates = (first: { dateUpdated: string }, second: { dateUpdated: string }) => {
  const firstDate = new Date(first.dateUpdated);
  const secondDate = new Date(second.dateUpdated);
  const isSecondMoreRecent = isAfter(secondDate, firstDate);

  return isSecondMoreRecent ? 1 : -1;
};

const DuplicateListingDetails = () => {
  const { paths, brand } = useContext(RMWContext);

  const {
    properties: {
      currentProperty: {
        isFeed,
        topDupesDetails: { isTopDupe, dupes, additionalListingSources, topDupeAliasEncoded },
      },
    },
  } = useSelector((state) => state);

  const isSingleSource = additionalListingSources.length === 1;
  const manualDupeToShow =
    isFeed &&
    dupes
      .sort(compareDates)
      .find(
        (dupe: { feedName: string }) =>
          dupe.feedName === brandConstants[brand as keyof typeof brandConstants] ||
          dupe.feedName === 'Zillow Rental Manager',
      );

  if (!isTopDupe) {
    return (
      <DuplicateListingDetailsContainer>
        <TextLinkContent
          text={DEMOTED_LISTING_COPY}
          href={`${paths.propertiesPage}/${topDupeAliasEncoded}/${paths.propertyDetailsListing}`}
          cta="View higher quality listing."
        />
      </DuplicateListingDetailsContainer>
    );
  }

  if (isFeed) {
    if (!manualDupeToShow) {
      return null;
    }

    return (
      <DuplicateListingDetailsContainer>
        <TextLinkContent
          text="Edit your "
          href={`${paths.propertiesPage}/${manualDupeToShow.aliasEncoded}/${paths.propertyDetailsListing}`}
          cta={`${StringConstants.FORMATTED_BRAND_NAMES_RENTAL[brand as keyof typeof brandConstants]} listing.`}
        />
      </DuplicateListingDetailsContainer>
    );
  }

  // If on manual listing, show list of feed sources
  if (additionalListingSources.length !== 0) {
    return (
      <DuplicateListingDetailsContainer>
        <Text data-test="not-posted">
          {`There ${isSingleSource ? 'is an alternative version' : 'are alternative versions'} of
                    this listing being received from the following source${isSingleSource ? '' : 's'},
                    but ${isSingleSource ? 'that version is' : 'those versions are'} not currently posted:`}
        </Text>
        <List.Root appearance="bullet" css={{ paddingLeft: '35px', listStyle: 'initial' }}>
          {cloneDeep(additionalListingSources)
            .sort()
            .map((source: string, i: number) => (
              <List.Item key={i} css={{ paddingTop: '250' }}>
                {source}
              </List.Item>
            ))}
        </List.Root>
      </DuplicateListingDetailsContainer>
    );
  }

  return null;
};

export default DuplicateListingDetails;
